import type { CreaseAssignment, CreasePattern } from '@/origami/types';
import { ASSIGNMENT_COLOR } from '@/origami/types';
import { planarize } from '@/origami/planarize';
import { boundingBox } from '@/geometry/polygon';
import type { Vec2 } from '@/geometry/vec2';

/**
 * SVG import/export for crease patterns.
 *
 * Import understands <line>, <polyline>, <polygon> and the straight-segment
 * subset of <path> (M/L/H/V/Z, absolute and relative). Curves are reported
 * rather than silently flattened - a Bezier is not a crease.
 *
 * Assignment comes from the stroke colour, matched against the standard origami
 * palette plus the common conventions used by other tools. Anything unmatched is
 * imported as unassigned and listed in the report so the UI can ask the user.
 */

const COLOR_MAP: { test: (hex: string) => boolean; assignment: CreaseAssignment }[] = [
  { test: (h) => isRed(h), assignment: 'M' },
  { test: (h) => isBlue(h), assignment: 'V' },
  { test: (h) => isGreen(h), assignment: 'C' },
  { test: (h) => isBlack(h), assignment: 'B' },
  { test: (h) => isGrey(h), assignment: 'F' },
];

function parseColor(raw: string | null): string | null {
  if (!raw) return null;
  const s = raw.trim().toLowerCase();
  if (s === 'none') return null;
  const named: Record<string, string> = {
    red: '#ff0000',
    blue: '#0000ff',
    green: '#008000',
    black: '#000000',
    grey: '#808080',
    gray: '#808080',
    magenta: '#ff00ff',
    cyan: '#00ffff',
  };
  if (named[s]) return named[s];
  const rgb = s.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
  if (rgb) {
    return `#${[1, 2, 3]
      .map((i) => Number(rgb[i]).toString(16).padStart(2, '0'))
      .join('')}`;
  }
  if (s.startsWith('#')) {
    if (s.length === 4) return `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`;
    if (s.length === 7) return s;
  }
  return s;
}

const rgbOf = (hex: string): [number, number, number] => [
  parseInt(hex.slice(1, 3), 16),
  parseInt(hex.slice(3, 5), 16),
  parseInt(hex.slice(5, 7), 16),
];

function isRed(hex: string): boolean {
  const [r, g, b] = rgbOf(hex);
  return r > 120 && r > g * 1.6 && r > b * 1.6;
}
function isBlue(hex: string): boolean {
  const [r, g, b] = rgbOf(hex);
  return b > 120 && b > r * 1.4 && b > g * 1.2;
}
function isGreen(hex: string): boolean {
  const [r, g, b] = rgbOf(hex);
  return g > 100 && g > r * 1.5 && g > b * 1.3;
}
function isBlack(hex: string): boolean {
  const [r, g, b] = rgbOf(hex);
  return r < 60 && g < 60 && b < 60;
}
function isGrey(hex: string): boolean {
  const [r, g, b] = rgbOf(hex);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max - min < 24 && r >= 60 && r <= 220;
}

export interface SvgImportReport {
  segments: number;
  /** Stroke colours that matched no assignment rule. */
  unmapped: string[];
  /** Elements we skipped, with a reason. */
  skipped: string[];
}

export interface SvgImportResult {
  pattern: CreasePattern;
  report: SvgImportReport;
}

interface RawLine {
  a: Vec2;
  b: Vec2;
  color: string | null;
}

/**
 * Parse SVG text. Uses DOMParser in the browser and a small regex reader in
 * Node (tests), because pulling a DOM implementation into the bundle for one
 * importer is not a good trade.
 */
export function svgToPattern(
  text: string,
  options: { flipY?: boolean; normalize?: boolean } = {},
): SvgImportResult {
  const report: SvgImportReport = { segments: 0, unmapped: [], skipped: [] };
  const lines: RawLine[] = [];

  const elements = readElements(text);
  for (const el of elements) {
    const color = parseColor(el.attrs.stroke ?? styleStroke(el.attrs.style));
    switch (el.tag) {
      case 'line': {
        const a = { x: num(el.attrs.x1), y: num(el.attrs.y1) };
        const b = { x: num(el.attrs.x2), y: num(el.attrs.y2) };
        lines.push({ a, b, color });
        break;
      }
      case 'polyline':
      case 'polygon': {
        const pts = numberList(el.attrs.points ?? '');
        for (let i = 0; i + 3 < pts.length; i += 2) {
          lines.push({
            a: { x: pts[i], y: pts[i + 1] },
            b: { x: pts[i + 2], y: pts[i + 3] },
            color,
          });
        }
        if (el.tag === 'polygon' && pts.length >= 4) {
          lines.push({
            a: { x: pts[pts.length - 2], y: pts[pts.length - 1] },
            b: { x: pts[0], y: pts[1] },
            color,
          });
        }
        break;
      }
      case 'rect': {
        const x = num(el.attrs.x);
        const y = num(el.attrs.y);
        const w = num(el.attrs.width);
        const h = num(el.attrs.height);
        const corners = [
          { x, y },
          { x: x + w, y },
          { x: x + w, y: y + h },
          { x, y: y + h },
        ];
        for (let i = 0; i < 4; i++) {
          lines.push({ a: corners[i], b: corners[(i + 1) % 4], color });
        }
        break;
      }
      case 'path': {
        const d = el.attrs.d ?? '';
        if (/[CcSsQqTtAa]/.test(d)) {
          report.skipped.push('A <path> containing curves was skipped: creases must be straight.');
          break;
        }
        for (const seg of pathToSegments(d)) lines.push({ ...seg, color });
        break;
      }
      default:
        break;
    }
  }

  const assignments: CreaseAssignment[] = [];
  const coords: Vec2[] = [];
  const edges: [number, number][] = [];
  for (const line of lines) {
    if (!Number.isFinite(line.a.x) || !Number.isFinite(line.b.x)) continue;
    if (line.a.x === line.b.x && line.a.y === line.b.y) continue;
    let assignment: CreaseAssignment = 'U';
    if (line.color) {
      const match = COLOR_MAP.find((m) => m.test(line.color as string));
      if (match) assignment = match.assignment;
      else if (!report.unmapped.includes(line.color)) report.unmapped.push(line.color);
    }
    const ai = coords.push(line.a) - 1;
    const bi = coords.push(line.b) - 1;
    edges.push([ai, bi]);
    assignments.push(assignment);
    report.segments++;
  }

  // SVG's y axis points down; the crease-pattern plane's points up.
  const flip = options.flipY ?? true;
  let working = coords;
  if (flip || options.normalize !== false) {
    const bb = boundingBox(coords);
    const scale = options.normalize === false ? 1 : 1 / Math.max(bb.width, bb.height, 1e-12);
    working = coords.map((c) => ({
      x: (c.x - bb.min.x) * scale,
      y: flip ? (bb.max.y - c.y) * scale : (c.y - bb.min.y) * scale,
    }));
  }

  const { pattern } = planarize({
    verticesCoords: working,
    edgesVertices: edges,
    edgesAssignment: assignments,
    meta: { title: 'Imported SVG', source: 'SVG import' },
  });
  return { pattern, report };
}

function styleStroke(style?: string): string | null {
  if (!style) return null;
  const m = style.match(/stroke\s*:\s*([^;]+)/i);
  return m ? m[1] : null;
}

const num = (s?: string): number => (s === undefined ? 0 : Number.parseFloat(s));
const numberList = (s: string): number[] =>
  s
    .trim()
    .split(/[\s,]+/)
    .map(Number)
    .filter((n) => Number.isFinite(n));

function pathToSegments(d: string): { a: Vec2; b: Vec2 }[] {
  const out: { a: Vec2; b: Vec2 }[] = [];
  const tokens = d.match(/[MmLlHhVvZz]|-?\d*\.?\d+(?:e[-+]?\d+)?/gi) ?? [];
  let i = 0;
  let cur: Vec2 = { x: 0, y: 0 };
  let start: Vec2 = { x: 0, y: 0 };
  let cmd = '';
  while (i < tokens.length) {
    const t = tokens[i];
    if (/[MmLlHhVvZz]/.test(t)) {
      cmd = t;
      i++;
      if (cmd === 'Z' || cmd === 'z') {
        out.push({ a: cur, b: start });
        cur = start;
      }
      continue;
    }
    const rel = cmd === cmd.toLowerCase();
    if (cmd === 'M' || cmd === 'm') {
      const x = Number(tokens[i++]);
      const y = Number(tokens[i++]);
      cur = rel ? { x: cur.x + x, y: cur.y + y } : { x, y };
      start = cur;
      cmd = rel ? 'l' : 'L';
    } else if (cmd === 'L' || cmd === 'l') {
      const x = Number(tokens[i++]);
      const y = Number(tokens[i++]);
      const next = rel ? { x: cur.x + x, y: cur.y + y } : { x, y };
      out.push({ a: cur, b: next });
      cur = next;
    } else if (cmd === 'H' || cmd === 'h') {
      const x = Number(tokens[i++]);
      const next = { x: rel ? cur.x + x : x, y: cur.y };
      out.push({ a: cur, b: next });
      cur = next;
    } else if (cmd === 'V' || cmd === 'v') {
      const y = Number(tokens[i++]);
      const next = { x: cur.x, y: rel ? cur.y + y : y };
      out.push({ a: cur, b: next });
      cur = next;
    } else {
      i++;
    }
  }
  return out;
}

interface SvgElement {
  tag: string;
  attrs: Record<string, string>;
}

function readElements(text: string): SvgElement[] {
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
    const out: SvgElement[] = [];
    for (const node of Array.from(doc.querySelectorAll('line,polyline,polygon,rect,path'))) {
      const attrs: Record<string, string> = {};
      for (const a of Array.from(node.attributes)) attrs[a.name] = a.value;
      out.push({ tag: node.tagName.toLowerCase(), attrs });
    }
    return out;
  }
  const out: SvgElement[] = [];
  const tagRe = /<(line|polyline|polygon|rect|path)\b([^>]*)\/?>/gi;
  let m: RegExpExecArray | null;
  while ((m = tagRe.exec(text)) !== null) {
    const attrs: Record<string, string> = {};
    const attrRe = /([\w:-]+)\s*=\s*"([^"]*)"|([\w:-]+)\s*=\s*'([^']*)'/g;
    let a: RegExpExecArray | null;
    while ((a = attrRe.exec(m[2])) !== null) {
      attrs[(a[1] ?? a[3]).toLowerCase()] = a[2] ?? a[4];
    }
    out.push({ tag: m[1].toLowerCase(), attrs });
  }
  return out;
}

export interface SvgExportOptions {
  size?: number;
  strokeWidth?: number;
  includeAuxiliary?: boolean;
  background?: string | null;
}

/** Export the flat crease pattern as SVG, using the standard crease colours. */
export function patternToSvg(
  pattern: CreasePattern,
  options: SvgExportOptions = {},
): string {
  const size = options.size ?? 1000;
  const bb = boundingBox(pattern.verticesCoords);
  const scale = size / Math.max(bb.width, bb.height, 1e-12);
  const w = Math.max(1, bb.width * scale);
  const h = Math.max(1, bb.height * scale);
  const strokeWidth = options.strokeWidth ?? Math.max(0.5, size / 600);

  const project = (v: Vec2): [number, number] => [
    (v.x - bb.min.x) * scale,
    // Flip back into SVG's y-down space so the export matches the editor.
    (bb.max.y - v.y) * scale,
  ];

  const parts: string[] = [];
  parts.push(
    `<svg xmlns="http://www.w3.org/2000/svg" width="${w.toFixed(2)}" height="${h.toFixed(
      2,
    )}" viewBox="0 0 ${w.toFixed(2)} ${h.toFixed(2)}">`,
  );
  parts.push(`<title>${escapeXml(pattern.meta.title ?? 'Crease pattern')}</title>`);
  if (options.background !== null) {
    parts.push(
      `<rect width="${w.toFixed(2)}" height="${h.toFixed(2)}" fill="${
        options.background ?? '#ffffff'
      }"/>`,
    );
  }
  for (let e = 0; e < pattern.edgesVertices.length; e++) {
    const assignment = pattern.edgesAssignment[e];
    if (assignment === 'J' && !options.includeAuxiliary) continue;
    const [a, b] = pattern.edgesVertices[e];
    const [x1, y1] = project(pattern.verticesCoords[a]);
    const [x2, y2] = project(pattern.verticesCoords[b]);
    const dash = assignment === 'C' ? ' stroke-dasharray="8 6"' : '';
    parts.push(
      `<line x1="${x1.toFixed(3)}" y1="${y1.toFixed(3)}" x2="${x2.toFixed(3)}" y2="${y2.toFixed(
        3,
      )}" stroke="${ASSIGNMENT_COLOR[assignment]}" stroke-width="${strokeWidth.toFixed(
        2,
      )}" stroke-linecap="round"${dash} data-assignment="${assignment}"/>`,
    );
  }
  parts.push('</svg>');
  return parts.join('\n');
}

const escapeXml = (s: string): string =>
  s.replace(/[<>&'"]/g, (c) =>
    ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' })[c] as string,
  );
